import { useState, useEffect, useRef } from "react";
/**
 * Custom hook for managing debounced text filter state
 * Syncs local state with column filter value and handles debounced updates
 */ export function useDebouncedTextFilter(column, debounceMs = 300) {
    const columnFilterValue = column.getFilterValue();
    const [value, setValue] = useState(columnFilterValue ?? "");
    const valueRef = useRef(value);
    useEffect(function syncValueRef() {
        valueRef.current = value;
    }, [
        value
    ]);
    useEffect(function debounceTextFilterUpdate() {
        const timeout = setTimeout(()=>{
            column.setFilterValue(value || undefined);
        }, debounceMs);
        return ()=>clearTimeout(timeout);
    }, [
        value,
        column,
        debounceMs
    ]);
    // External changes come e.g. from the URL or from clearing filters.
    useEffect(function syncTextFromExternalFilter() {
        const normalizedExternal = columnFilterValue ?? "";
        if (normalizedExternal !== valueRef.current) {
            setValue(normalizedExternal);
        }
    }, [
        columnFilterValue
    ]);
    return [
        value,
        setValue
    ];
}
/**
 * Custom hook for managing debounced date range filter state
 */ export function useDebouncedDateRangeFilter(column, debounceMs = 300) {
    const columnFilterValue = column.getFilterValue();
    const [fromDate, setFromDate] = useState(columnFilterValue?.from ?? "");
    const [toDate, setToDate] = useState(columnFilterValue?.to ?? "");
    const fromDateRef = useRef(fromDate);
    const toDateRef = useRef(toDate);
    useEffect(function syncFromDateRef() {
        fromDateRef.current = fromDate;
    }, [
        fromDate
    ]);
    useEffect(function syncToDateRef() {
        toDateRef.current = toDate;
    }, [
        toDate
    ]);
    useEffect(function debounceDateRangeFilterUpdate() {
        const timeout = setTimeout(()=>{
            const newValue = fromDate || toDate ? {
                from: fromDate || undefined,
                to: toDate || undefined
            } : undefined;
            column.setFilterValue(newValue);
        }, debounceMs);
        return ()=>clearTimeout(timeout);
    }, [
        fromDate,
        toDate,
        column,
        debounceMs
    ]);
    // External changes come e.g. from the URL or from clearing filters.
    useEffect(function syncDateRangeFromExternalFilter() {
        const externalFrom = columnFilterValue?.from ?? "";
        const externalTo = columnFilterValue?.to ?? "";
        if (externalFrom !== fromDateRef.current) {
            setFromDate(externalFrom);
        }
        if (externalTo !== toDateRef.current) {
            setToDate(externalTo);
        }
    }, [
        columnFilterValue
    ]);
    return {
        fromDate,
        setFromDate,
        toDate,
        setToDate
    };
}
/**
 * Custom hook for managing debounced number range filter state
 */ export function useDebouncedNumberRangeFilter(column, debounceMs = 300) {
    const columnFilterValue = column.getFilterValue();
    const [min, setMin] = useState(columnFilterValue?.min?.toString() ?? "");
    const [max, setMax] = useState(columnFilterValue?.max?.toString() ?? "");
    const minRef = useRef(min);
    const maxRef = useRef(max);
    useEffect(function syncMinRef() {
        minRef.current = min;
    }, [
        min
    ]);
    useEffect(function syncMaxRef() {
        maxRef.current = max;
    }, [
        max
    ]);
    useEffect(function debounceNumberRangeFilterUpdate() {
        const timeout = setTimeout(()=>{
            const minNum = min ? parseFloat(min) : undefined;
            const maxNum = max ? parseFloat(max) : undefined;
            const newValue = minNum !== undefined || maxNum !== undefined ? {
                min: minNum,
                max: maxNum
            } : undefined;
            column.setFilterValue(newValue);
        }, debounceMs);
        return ()=>clearTimeout(timeout);
    }, [
        min,
        max,
        column,
        debounceMs
    ]);
    // External changes come e.g. from the URL or from clearing filters.
    useEffect(function syncNumberRangeFromExternalFilter() {
        const externalMin = columnFilterValue?.min?.toString() ?? "";
        const externalMax = columnFilterValue?.max?.toString() ?? "";
        if (externalMin !== minRef.current) {
            setMin(externalMin);
        }
        if (externalMax !== maxRef.current) {
            setMax(externalMax);
        }
    }, [
        columnFilterValue
    ]);
    return {
        min,
        setMin,
        max,
        setMax
    };
}

//# sourceMappingURL=hooks.js.map